<template>
  <div class="content">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row" style="width: 100%;">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">Admin Panel - Permission Management</h4>
              <p class="card-category">Manage roles, group mappings, and permissions</p>
            </div>
            <div class="card-body">
              <!-- Cache Status -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="alert alert-info">
                    <strong>Cache Status:</strong> 
                    {{ cacheStatus.isInitialized ? 'Initialized' : 'Not Initialized' }} | 
                    {{ cacheStatus.permissionCount }} permissions cached
                  </div>
                </div>
                <div class="col-md-6 text-right">
                  <button @click="refreshCache" class="btn btn-sm btn-warning" :disabled="refreshing">
                    <i class="fas fa-sync-alt"></i> {{ refreshing ? 'Refreshing...' : 'Refresh Cache' }}
                  </button>
                </div>
              </div>

              <!-- Tabs -->
              <ul class="nav nav-tabs" id="adminTabs" role="tablist">
                <li class="nav-item">
                  <a class="nav-link active" id="roles-tab" data-toggle="tab" href="#roles" role="tab">
                    Roles & Permissions
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" id="permission-types-tab" data-toggle="tab" href="#permission-types" role="tab">
                    Permission Types
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" id="groups-tab" data-toggle="tab" href="#groups" role="tab">
                    Group to Role Mappings
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" id="group-apps-tab" data-toggle="tab" href="#group-apps" role="tab">
                    Group to App Access Mappings
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" id="email-mappings-tab" data-toggle="tab" href="#email-mappings" role="tab">
                    Email to Role Mappings
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" id="email-app-access-tab" data-toggle="tab" href="#email-app-access" role="tab">
                    Email to App Access Mappings
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" id="retention-admin-tab" data-toggle="tab" href="#retention-admin" role="tab">
                    Retention Messaging
                  </a>
                </li>
              </ul>

              <!-- Tab Content -->
              <div class="tab-content" id="adminTabContent">
                <!-- Roles & Permissions Tab -->
                <div class="tab-pane fade show active" id="roles" role="tabpanel">
                  <div class="table-responsive mt-3">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>Role Name</th>
                          <th>Display Name</th>
                          <th>Description</th>
                          <th>Permissions</th>
                          <th>App Access</th>
                          <th>Environment Access</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="permission in permissionList" :key="permission.id">
                          <td>{{ permission.role_name || permission.roleName || permission.id }}</td>
                          <td>{{ permission.name || 'N/A' }}</td>
                          <td>{{ permission.description || 'No description' }}</td>
                          <td>
                            <span class="badge badge-info mr-1" v-for="perm in permission.permissions" :key="perm">
                              {{ perm }}
                            </span>
                          </td>
                          <td>
                            <span class="badge badge-success">{{ permission.app_access || permission.appAccess || 'assigned' }}</span>
                          </td>
                          <td>
                            <span 
                              v-for="( env) in permission.environment_access" 
                              :key="env"
                              v-if="env ? true : false"
                              class="badge badge-info mr-1"
                            >
                              {{ env }}
                            </span>
                          </td>
                          <td>
                            <button @click="editPermission(permission)" class="btn btn-sm btn-primary mr-1">
                              <i class="fas fa-edit"></i>
                            </button>
                            <button @click="deletePermission(permission.id)" class="btn btn-sm btn-danger">
                              <i class="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="text-right mt-3">
                    <button @click="showAddPermissionModal" class="btn btn-success">
                      <i class="fas fa-plus"></i> Add New Role
                    </button>
                  </div>
                </div>

                <!-- Permission Types Tab -->
                <div class="tab-pane fade" id="permission-types" role="tabpanel">
                  <permission-type-manager />
                </div>

                <!-- Group to Role Mappings Tab -->
                <div class="tab-pane fade" id="groups" role="tabpanel">
                  <div class="table-responsive mt-3">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>Group Name</th>
                          <th>Mapped Role</th>
                          <th>Description</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="mapping in groupMappings" :key="mapping.id">
                          <td>{{ mapping.group_name || mapping.groupName }}</td>
                          <td>
                            <span class="badge badge-primary">{{ getRoleNameForDisplay(mapping) }}</span>
                          </td>
                          <td>{{ mapping.description || 'No description' }}</td>
                          <td>
                            <button @click="editGroupMapping(mapping)" class="btn btn-sm btn-primary mr-1">
                              <i class="fas fa-edit"></i>
                            </button>
                            <button @click="deleteGroupMapping(mapping.id)" class="btn btn-sm btn-danger">
                              <i class="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="text-right mt-3">
                    <button @click="showAddGroupMappingModal" class="btn btn-success">
                      <i class="fas fa-plus"></i> Add Group Mapping
                    </button>
                  </div>
                </div>

                <!-- Group to App Access Mappings Tab -->
                <div class="tab-pane fade" id="group-apps" role="tabpanel">
                  <div class="table-responsive mt-3">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>Group Name</th>
                          <th>Access Type</th>
                          <th>Apps</th>
                          <th>Description</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="mapping in groupAppMappings" :key="mapping.id">
                          <td>{{ mapping.group_name }}</td>
                          <td>
                            <span v-if="mapping.has_all_access" class="badge badge-success">All Apps</span>
                            <span v-else class="badge badge-primary">Specific Apps</span>
                          </td>
                          <td>
                            <div v-if="mapping.has_all_access">
                              <span class="text-success">Access to all applications</span>
                            </div>
                            <div v-else>
                              <div v-if="mapping.app_access && mapping.app_access.length > 0">
                                <span v-for="app in mapping.app_access" :key="app.app_id" class="badge badge-secondary mr-1">
                                  {{ app.app_name || app.app_id }}
                                </span>
                              </div>
                              <span v-else class="text-muted">No apps assigned</span>
                            </div>
                          </td>
                          <td>{{ mapping.description || 'No description' }}</td>
                          <td>
                            <button @click="editGroupAppMapping(mapping)" class="btn btn-sm btn-primary mr-1">
                              <i class="fas fa-edit"></i>
                            </button>
                            <button @click="deleteGroupAppMapping(mapping.id)" class="btn btn-sm btn-danger">
                              <i class="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="text-right mt-3">
                    <button @click="showAddGroupAppMappingModal" class="btn btn-success">
                      <i class="fas fa-plus"></i> Add Group App Mapping
                    </button>
                  </div>
                </div>

                <!-- Email to Role Mappings Tab -->
                <div class="tab-pane fade" id="email-mappings" role="tabpanel">
                  <div class="table-responsive mt-3">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>Email Address</th>
                          <th>Mapped Role</th>
                          <th>Description</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="mapping in emailMappings" :key="mapping.id">
                          <td>{{ mapping.email }}</td>
                          <td>
                            <span class="badge badge-primary">{{ mapping.role_name }}</span>
                          </td>
                          <td>{{ mapping.description || 'No description' }}</td>
                          <td>{{ mapping.created_by || 'System' }}</td>
                          <td>{{ formatDate(mapping.created_at) }}</td>
                          <td>
                            <button @click="editEmailMapping(mapping)" class="btn btn-sm btn-primary mr-1">
                              <i class="fas fa-edit"></i>
                            </button>
                            <button @click="deleteEmailMapping(mapping.id)" class="btn btn-sm btn-danger">
                              <i class="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="text-right mt-3">
                    <button @click="refreshEmailMappings" class="btn btn-sm btn-warning mr-2" :disabled="refreshing">
                      <i class="fas fa-sync-alt"></i> {{ refreshing ? 'Refreshing...' : 'Refresh' }}
                    </button>
                    <button @click="showAddEmailMappingModal" class="btn btn-success">
                      <i class="fas fa-plus"></i> Add Email Mapping
                    </button>
                  </div>
                </div>

                <!-- Email to App Access Mappings Tab -->
                <div class="tab-pane fade" id="email-app-access" role="tabpanel">
                  <div class="table-responsive mt-3">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>Email Address</th>
                          <th>Access Type</th>
                          <th>Apps</th>
                          <th>Description</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="mapping in emailAppAccessMappings" :key="mapping.id">
                          <td>{{ mapping.email }}</td>
                          <td>
                            <span v-if="mapping.has_all_access" class="badge badge-success">All Apps</span>
                            <span v-else class="badge badge-primary">Specific Apps</span>
                          </td>
                          <td>
                            <div v-if="mapping.has_all_access">
                              <span class="text-success">Access to all applications</span>
                            </div>
                            <div v-else>
                              <div v-if="mapping.app_access && mapping.app_access.length > 0">
                                <span v-for="app in mapping.app_access" :key="app.app_id" class="badge badge-secondary mr-1">
                                  {{ app.app_name || app.app_id }}
                                </span>
                              </div>
                              <span v-else class="text-muted">No apps assigned</span>
                            </div>
                          </td>
                          <td>{{ mapping.description || 'No description' }}</td>
                          <td>{{ mapping.created_by || 'System' }}</td>
                          <td>{{ formatDate(mapping.created_at) }}</td>
                          <td>
                            <button @click="editEmailAppAccessMapping(mapping)" class="btn btn-sm btn-primary mr-1">
                              <i class="fas fa-edit"></i>
                            </button>
                            <button @click="deleteEmailAppAccessMapping(mapping.id)" class="btn btn-sm btn-danger">
                              <i class="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="text-right mt-3">
                    <button @click="refreshEmailAppAccessMappings" class="btn btn-sm btn-warning mr-2" :disabled="refreshing">
                      <i class="fas fa-sync-alt"></i> {{ refreshing ? 'Refreshing...' : 'Refresh' }}
                    </button>
                    <button @click="showAddEmailAppAccessMappingModal" class="btn btn-success">
                      <i class="fas fa-plus"></i> Add Email App Access Mapping
                    </button>
                  </div>
                </div>

                <!-- Retention Messaging admin tab -->
                <div class="tab-pane fade" id="retention-admin" role="tabpanel">
                  <div class="mt-3">
                    <retention-admin-panel />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Permission Modal -->
    <div class="modal fade" id="permissionModal" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingPermission ? 'Edit Role' : 'Add New Role' }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="savePermission">
              <div class="form-group">
                <label>Role Name</label>
                <input 
                  v-model="permissionForm.role_name" 
                  type="text" 
                  class="form-control" 
                  required
                  :disabled="editingPermission"
                >
              </div>
              <div class="form-group">
                <label>Display Name</label>
                <input v-model="permissionForm.name" type="text" class="form-control" required>
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea v-model="permissionForm.description" class="form-control" rows="3"></textarea>
              </div>
              <div class="form-group">
                <label>Permissions</label>
                <div class="permission-buttons">
                  <button type="button" @click="selectAllPermissions" class="btn btn-sm btn-outline-primary mr-2">
                    Select All
                  </button>
                  <button type="button" @click="selectNonePermissions" class="btn btn-sm btn-outline-secondary mr-2">
                    Select None
                  </button>
                  <button type="button" @click="selectDefaultPermissions" class="btn btn-sm btn-outline-success">
                    Select Default (Non-Admin)
                  </button>
                </div>
                <div class="permission-categories permissions-grid">
                  <div class="permissions-column">
                    <div
                      v-for="category in leftCategories"
                      :key="category.name"
                      class="permission-category"
                    >
                      <h6>{{ category.name }}</h6>
                      <div
                        v-for="perm in category.permissions"
                        :key="perm"
                        class="form-check permission-item"
                      >
                        <label class="form-check-label">
                          <input
                            type="checkbox"
                            :value="perm"
                            v-model="permissionForm.permissions"
                            class="form-check-input"
                          />
                          {{ perm }}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="permissions-column">
                    <div
                      v-for="category in rightCategories"
                      :key="category.name"
                      class="permission-category"
                    >
                      <h6>{{ category.name }}</h6>
                      <div
                        v-for="perm in category.permissions"
                        :key="perm"
                        class="form-check permission-item"
                      >
                        <label class="form-check-label">
                          <input
                            type="checkbox"
                            :value="perm"
                            v-model="permissionForm.permissions"
                            class="form-check-input"
                          />
                          {{ perm }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label>App Access</label>
                <select v-model="permissionForm.app_access" class="form-control">
                  <option value="assigned">Assigned Apps Only</option>
                  <option value="all">All Apps</option>
                </select>
              </div>
              <div class="form-group">
                <label>Environment Access</label>
                <div class="form-check">
                  <label class="form-check-label">
                    <input
                      type="checkbox"
                      v-model="permissionForm.environment_access.dev"
                      class="form-check-input"
                    />
                    Development test
                  </label>
                </div>
                <div class="form-check">
                  <label class="form-check-label">
                    <input
                      type="checkbox"
                      v-model="permissionForm.environment_access.staging"
                      class="form-check-input"
                    />
                    Staging
                  </label>
                </div>
                <div class="form-check">
                  <label class="form-check-label">
                    <input
                      type="checkbox"
                      v-model="permissionForm.environment_access.prod"
                      class="form-check-input"
                    />
                    Production
                  </label>
                </div>
                <small class="form-text text-muted">
                  Select which environments this role can access. Production access is restricted to admin roles.
                </small>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button @click="savePermission" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Group Mapping Modal -->
    <div class="modal fade" id="groupMappingModal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingGroupMapping ? 'Edit Group Mapping' : 'Add Group Mapping' }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveGroupMapping">
              <div class="form-group">
                <label>Group Name</label>
                <input 
                  v-model="groupMappingForm.group_name" 
                  type="text" 
                  class="form-control" 
                  required
                  :disabled="editingGroupMapping"
                >
              </div>
              <div class="form-group">
                <label>Role</label>
                <select v-model="groupMappingForm.role_id" class="form-control" required>
                  <option value="">Select a role</option>
                  <option v-for="permission in permissionList" :key="permission.id" :value="permission.role_id || permission.id">
                    {{ permission.name || permission.role_name || permission.roleName || permission.id }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea v-model="groupMappingForm.description" class="form-control" rows="3"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button @click="saveGroupMapping" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Group App Mapping Modal -->
    <div class="modal fade" id="groupAppMappingModal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingGroupAppMapping ? 'Edit Group App Mapping' : 'Add Group App Mapping' }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveGroupAppMapping">
              <div class="form-group">
                <label>Group Name</label>
                <input 
                  v-model="groupAppMappingForm.group_name" 
                  type="text" 
                  class="form-control" 
                  required
                  :disabled="editingGroupAppMapping"
                >
              </div>
              <div class="form-group">
                <label>Access Type</label>
                <div class="form-check">
                  <input 
                    v-model="groupAppMappingForm.has_all_access" 
                    type="checkbox" 
                    class="form-check-input" 
                    id="hasAllAccess"
                    @change="onHasAllAccessChange"
                  >
                  <label class="form-check-label" for="hasAllAccess">
                    Has access to all apps
                  </label>
                </div>
              </div>
              <div class="form-group" v-if="!groupAppMappingForm.has_all_access">
                <label>App Access</label>
                <div class="alert alert-info mb-3">
                  <small>
                    Select specific apps that this group should have access to:
                  </small>
                </div>
                <select 
                  v-model="groupAppMappingForm.app_access" 
                  class="form-control" 
                  multiple 
                  size="8"
                  style="height: auto;"
                  :disabled="groupAppMappingForm.has_all_access"
                >
                  <option 
                    v-for="app in allAppsArray" 
                    :key="app.app_id" 
                    :value="app"
                  >
                    {{ app.app_name }} ({{ app.app_id }})
                  </option>
                  <!-- Fallback option if no apps loaded -->
                  <option v-if="allAppsArray.length === 0" disabled>
                    No apps available. Please refresh the page.
                  </option>
                </select>
                <small class="form-text text-muted">
                  Hold Ctrl (or Cmd on Mac) to select multiple apps
                </small>
              </div>
              
              <div class="form-group">
                <label>Description</label>
                <textarea v-model="groupAppMappingForm.description" class="form-control" rows="3"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button @click="saveGroupAppMapping" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Email App Access Mapping Modal -->
    <div class="modal fade" id="emailAppAccessMappingModal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingEmailAppAccessMapping ? 'Edit Email App Access Mapping' : 'Add Email App Access Mapping' }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveEmailAppAccessMapping">
              <div class="form-group">
                <label>Email Address</label>
                <input 
                  v-model="emailAppAccessMappingForm.email" 
                  type="email" 
                  class="form-control" 
                  required
                  :disabled="editingEmailAppAccessMapping"
                >
              </div>
              <div class="form-group">
                <label>Access Type</label>
                <div class="form-check">
                  <input 
                    v-model="emailAppAccessMappingForm.has_all_access" 
                    type="checkbox" 
                    class="form-check-input" 
                    id="emailHasAllAccess"
                    @change="onEmailHasAllAccessChange"
                  >
                  <label class="form-check-label" for="emailHasAllAccess">
                    Has access to all apps
                  </label>
                </div>
              </div>
              <div class="form-group" v-if="!emailAppAccessMappingForm.has_all_access">
                <label>App Access</label>
                <div class="alert alert-info mb-3">
                  <small>
                    Select specific apps that this email should have access to:
                  </small>
                </div>
                <select 
                  v-model="emailAppAccessMappingForm.app_access" 
                  class="form-control" 
                  multiple 
                  size="8"
                  style="height: auto;"
                  :disabled="emailAppAccessMappingForm.has_all_access"
                >
                  <option 
                    v-for="app in allAppsArray" 
                    :key="app.app_id" 
                    :value="app"
                  >
                    {{ app.app_name }} ({{ app.app_id }})
                  </option>
                  <!-- Fallback option if no apps loaded -->
                  <option v-if="allAppsArray.length === 0" disabled>
                    No apps available. Please refresh the page.
                  </option>
                </select>
                <small class="form-text text-muted">
                  Hold Ctrl (or Cmd on Mac) to select multiple apps
                </small>
              </div>
              
              <div class="form-group">
                <label>Description</label>
                <textarea v-model="emailAppAccessMappingForm.description" class="form-control" rows="3"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button @click="saveEmailAppAccessMapping" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Mapping Modal -->
    <div class="modal fade" id="emailMappingModal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingEmailMapping ? 'Edit Email Mapping' : 'Add Email Mapping' }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveEmailMapping">
              <div class="form-group">
                <label>Email Address</label>
                <input 
                  v-model="emailMappingForm.email" 
                  type="email" 
                  class="form-control" 
                  required
                  :disabled="editingEmailMapping"
                  placeholder="user@company.com"
                >
              </div>
              <div class="form-group">
                <label>Role</label>
                <select v-model="emailMappingForm.role_id" class="form-control" required>
                  <option value="">Select a role</option>
                  <option v-for="role in availableRoles" :key="role.role_id || role.id" :value="role.role_id || role.id">
                    {{ formatRoleName(role.role_name || role.name || role.id) }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea v-model="emailMappingForm.description" class="form-control" rows="3" placeholder="Reason for this mapping (optional)"></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button @click="saveEmailMapping" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { adminService } from '../services/admin.service';
import { managerService } from '../services/manager.service';
import permissionService from '../services/permission.service';
import PermissionTypeManager from '../components/admin/PermissionTypeManager.vue';
import RetentionAdminPanel from '../components/admin/RetentionAdminPanel.vue';
import store from '../store';

// Get the base URL from the manager service
const isLocal = window.location.href.indexOf('localhost') >= 0;
const managerBaseUrl = isLocal ? 'http://localhost:5001/manager' : '/manager';

export default {
  name: 'Admin',
  components: {
    PermissionTypeManager,
    RetentionAdminPanel
  },

  data() {
    return {
      permissionList: [],
      groupMappings: [],
      groupAppMappings: [],
      allApps: {}, // Store all available apps for dropdown
      cacheStatus: {
        isInitialized: false,
        permissionCount: 0
      },
      refreshing: false,
      saving: false,
      editingPermission: null,
      editingGroupMapping: null,
      editingGroupAppMapping: null,
      editingEmailMapping: null,
      permissionForm: {
        role_name: '',
        name: '',
        description: '',
        permissions: [],
        app_access: 'assigned',
        environment_access: {
          dev: true,
          staging: true,
          prod: false
        }
      },
      groupMappingForm: {
        group_name: '',
        role_id: '',
        description: ''
      },
      groupAppMappingForm: {
        group_name: '',
        app_access: [],
        has_all_access: false,
        description: ''
      },
      emailMappings: [],
      emailMappingForm: {
        email: '',
        role_id: '',
        description: ''
      },
      emailAppAccessMappings: [],
      editingEmailAppAccessMapping: null,
      emailAppAccessMappingForm: {
        email: '',
        app_access: [],
        has_all_access: false,
        description: ''
      },
      // permissionCategories is now a computed property that gets data from the store dynamically
    };
  },
  watch: {
    'permissionForm.permissions': {
      handler(newPermissions) {
        // Only update environment access automatically when NOT editing an existing permission
        if (!this.editingPermission) {
          this.updateEnvironmentAccess();
        }
      },
      deep: true
    },
    'permissionForm.environment_access': {
      handler(newValue, oldValue) {
        console.log('🔍 [Admin.vue] Environment access changed:', { newValue, oldValue });
      },
      deep: true
    }
  },
  async mounted() {
    await this.checkAccess();
    
    // Add a small delay to ensure authentication context is properly established
    await new Promise(resolve => setTimeout(resolve, 100));
    
    await this.loadData();
    
    // Fetch permission types for the Permission Types tab
    await this.$store.dispatch('fetchPermissionTypes');
  },
  computed: {
    /**
     * Dynamically build permission categories from the store
     * Groups permission types by category and returns in the format needed for the UI
     */
    permissionCategories() {
      const permissionTypes = this.$store.getters.permissionTypes || [];
      
      // Group permission types by category
      const grouped = {};
      permissionTypes
        .filter(pt => pt.is_active !== false) // Only show active permissions
        .forEach(pt => {
          if (!grouped[pt.category]) {
            grouped[pt.category] = {
              name: pt.category,
              permissions: []
            };
          }
          grouped[pt.category].permissions.push(pt.key);
        });
      
      // Convert to array and sort by category name
      const categories = Object.values(grouped).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      
      // Sort permissions within each category
      categories.forEach(cat => {
        cat.permissions.sort();
      });
      
      return categories;
    },
    leftCategories() {
      const mid = Math.ceil(this.permissionCategories.length / 2);
      return this.permissionCategories.slice(0, mid);
    },
    rightCategories() {
      const mid = Math.ceil(this.permissionCategories.length / 2);
      return this.permissionCategories.slice(mid);
    },
    // Convert allApps object to array for easier handling
    allAppsArray() {
      return Object.entries(this.allApps).map(([appId, appName]) => ({
        app_id: appId,
        app_name: appName
      }));
    },
    // Available roles for email mapping - use actual fetched roles
    availableRoles() {
      if (!this.permissionList || this.permissionList.length === 0) {
        return [];
      }
      
      // Return role objects with both role_id and role_name
      return this.permissionList.map(permission => {
        return {
          role_id: permission.role_id || permission.id,
          role_name: permission.role_name || permission.roleName || permission.name || permission.id,
          name: permission.name || permission.role_name || permission.roleName || permission.id
        };
      }).filter(role => role.role_id); // Remove any undefined/null values
    }
  },
  methods: {
    // Safe toast method that handles cases where toast is not available
    safeToast(type, message) {
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to console and alert
        console.log(`${type.toUpperCase()}: ${message}`);
        if (type === 'error') {
          alert(`Error: ${message}`);
        }
      }
    },

    async checkAccess() {
      // Force refresh user permissions to get latest email-based permissions
      await permissionService.forceRefreshPermissions();
      
      const userPermissions = await permissionService.fetchUserPermissions();
      const isAdmin = permissionService.isAdmin();
      const hasConfigManagerRole = permissionService.hasPermission('admin.roles');
      
      if (!isAdmin && !hasConfigManagerRole) {
        this.$router.push('/');
        return;
      }
    },
    
    async loadData() {
      try {
        await this.loadPermissions();
        await this.loadCacheStatus();
        await this.loadGroupMappings();
        await this.loadGroupAppMappings();
        await this.loadEmailMappings();
        await this.loadEmailAppAccessMappings();
        await this.loadAllApps();
      } catch (error) {
        console.error('Error loading admin data:', error);
      }
    },
    
    async loadPermissions() {
      try {
        const response = await adminService.getAllPermissions();
        if (response.data && response.data.status === 200) {
          this.permissionList = response.data.result || [];
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
        
        // If it's a 403 error, try refreshing permissions and retry once
        if (error.response && error.response.status === 403) {
          try {
            await permissionService.forceRefreshPermissions();
            await new Promise(resolve => setTimeout(resolve, 200)); // Wait a bit longer
            
            const retryResponse = await adminService.getAllPermissions();
            if (retryResponse.data && retryResponse.data.status === 200) {
              this.permissionList = retryResponse.data.result || [];
              return;
            }
          } catch (retryError) {
            console.error('Retry also failed:', retryError);
          }
        }
        
        // Fallback to empty list
        this.permissionList = [];
      }
    },
    
    async loadCacheStatus() {
      try {
        const response = await adminService.getCacheStatus();
        if (response.data && response.data.status === 200) {
          this.cacheStatus = response.data.result;
        }
      } catch (error) {
        console.error('Error loading cache status:', error);
      }
    },
    
    async loadGroupMappings() {
      try {
        const response = await adminService.getAllGroupMappings();
        
        if (response.data && response.data.status === 200) {
          this.groupMappings = response.data.result || [];
        } else {
          // Fallback to mock data if API is not available
          this.groupMappings = [
            { id: 'mock1', group_name: 'admin', role_name: 'admin' },
            { id: 'mock2', group_name: 'super-admin', role_name: 'super_admin' },
            { id: 'mock3', group_name: 'marketing-manager', role_name: 'marketing_manager' },
            { id: 'mock4', group_name: 'marketing-specialist', role_name: 'marketing_specialist' },
            { id: 'mock5', group_name: 'analyst', role_name: 'analyst' },
            { id: 'mock6', group_name: 'developer', role_name: 'developer' },
            { id: 'mock7', group_name: 'cerebro_junior_developers', role_name: 'developer' },
            { id: 'mock8', group_name: 'Everyone', role_name: 'viewer' }
          ];
        }
      } catch (error) {
        console.error('Error loading group mappings:', error);
        // Fallback to mock data
        this.groupMappings = [
          { id: 'mock1', group_name: 'admin', role_name: 'admin' },
          { id: 'mock2', group_name: 'super-admin', role_name: 'super_admin' },
          { id: 'mock3', group_name: 'marketing-manager', role_name: 'marketing_manager' },
          { id: 'mock4', group_name: 'marketing-specialist', role_name: 'marketing_specialist' },
          { id: 'mock5', group_name: 'analyst', role_name: 'analyst' },
          { id: 'mock6', group_name: 'developer', role_name: 'developer' },
          { id: 'mock7', group_name: 'cerebro_junior_developers', role_name: 'developer' },
          { id: 'mock8', group_name: 'Everyone', role_name: 'viewer' }
        ];
      }
    },
    
    async loadGroupAppMappings() {
      try {
        const response = await adminService.getAllGroupAppMappings();
        
        if (response.data && response.data.status === 200) {
          this.groupAppMappings = response.data.result || [];
        } else {
          // Fallback to mock data if API is not available
          this.groupAppMappings = [
            { id: 'mock1', group_name: 'admin', has_all_access: true, app_access: [], description: 'Admin group with all access' },
            { id: 'mock2', group_name: 'super-admin', has_all_access: false, app_access: [{ app_id: 'app2', app_name: 'App 2' }], description: 'Super admin with specific access' },
            { id: 'mock3', group_name: 'marketing-manager', has_all_access: true, app_access: [], description: 'Marketing manager with all access' },
            { id: 'mock4', group_name: 'marketing-specialist', has_all_access: false, app_access: [{ app_id: 'app4', app_name: 'App 4' }], description: 'Marketing specialist with specific access' },
            { id: 'mock5', group_name: 'analyst', has_all_access: true, app_access: [], description: 'Analyst with all access' },
            { id: 'mock6', group_name: 'developer', has_all_access: false, app_access: [{ app_id: 'app6', app_name: 'App 6' }], description: 'Developer with specific access' },
            { id: 'mock7', group_name: 'cerebro_junior_developers', has_all_access: true, app_access: [], description: 'Junior developers with all access' },
            { id: 'mock8', group_name: 'Everyone', has_all_access: false, app_access: [{ app_id: 'app8', app_name: 'App 8' }], description: 'Everyone with specific access' }
          ];
        }
      } catch (error) {
        console.error('Error loading group app mappings:', error);
        // Fallback to mock data
        this.groupAppMappings = [
          { id: 'mock1', group_name: 'admin', has_all_access: true, app_access: [], description: 'Admin group with all access' },
          { id: 'mock2', group_name: 'super-admin', has_all_access: false, app_access: [{ app_id: 'app2', app_name: 'App 2' }], description: 'Super admin with specific access' },
          { id: 'mock3', group_name: 'marketing-manager', has_all_access: true, app_access: [], description: 'Marketing manager with all access' },
          { id: 'mock4', group_name: 'marketing-specialist', has_all_access: false, app_access: [{ app_id: 'app4', app_name: 'App 4' }], description: 'Marketing specialist with specific access' },
          { id: 'mock5', group_name: 'analyst', has_all_access: true, app_access: [], description: 'Analyst with all access' },
          { id: 'mock6', group_name: 'developer', has_all_access: false, app_access: [{ app_id: 'app6', app_name: 'App 6' }], description: 'Developer with specific access' },
          { id: 'mock7', group_name: 'cerebro_junior_developers', has_all_access: true, app_access: [], description: 'Junior developers with all access' },
          { id: 'mock8', group_name: 'Everyone', has_all_access: false, app_access: [{ app_id: 'app8', app_name: 'App 8' }], description: 'Everyone with specific access' }
        ];
      }
    },
    
    async loadAllApps() {
      try {
        const response = await adminService.getAllApps();
        
        if (response.data && response.data.status === 200) {
          this.allApps = response.data.result || {};
        } else {
          this.allApps = {};
        }
      } catch (error) {
        console.error('Error loading all apps:', error);
        this.allApps = {};
      }
    },
    
    
    async refreshCache() {
      this.refreshing = true;
      try {
        await adminService.refreshCache();
        // Reload all data after cache refresh
        await Promise.all([
          this.loadCacheStatus(),
          this.loadPermissions(),
          this.loadGroupMappings(),
          this.loadGroupAppMappings(),
          this.loadAllApps()
        ]);
        this.safeToast('success', 'Cache and data refreshed successfully');
      } catch (error) {
        console.error('Error refreshing cache and data:', error);
        this.safeToast('error', 'Failed to refresh cache and data');
      } finally {
        this.refreshing = false;
      }
    },
    
    showAddPermissionModal() {
      this.editingPermission = null;
      this.resetPermissionForm();
      $('#permissionModal').modal('show');
    },
    
          editPermission(permission) {
        this.editingPermission = permission;
        
        console.log('🔍 [Admin.vue] Original permission:', permission);
        console.log('🔍 [Admin.vue] Original environment_access:', permission.environment_access);
        
        // Convert array to object for environment access
        const environmentAccess = this.convertEnvironmentAccessToObject(permission.environment_access);
        console.log('🔍 [Admin.vue] Converted environment_access:', environmentAccess);
        
        // Clear the form first
        this.permissionForm = {
          role_name: '',
          name: '',
          description: '',
          permissions: [],
          app_access: 'assigned',
          environment_access: { dev: false, staging: false, prod: false }
        };
        
        // Then set the values in next tick
        this.$nextTick(() => {
          this.$set(this.permissionForm, 'role_name', permission.role_name || permission.roleName || permission.id);
          this.$set(this.permissionForm, 'name', permission.name || '');
          this.$set(this.permissionForm, 'description', permission.description || '');
          this.$set(this.permissionForm, 'permissions', permission.permissions || []);
          this.$set(this.permissionForm, 'app_access', permission.app_access || permission.appAccess || 'assigned');
          this.$set(this.permissionForm, 'environment_access', environmentAccess);
          
          console.log('🔍 [Admin.vue] Final permissionForm:', this.permissionForm);
          console.log('🔍 [Admin.vue] Environment access object:', this.permissionForm.environment_access);
          
          $('#permissionModal').modal('show');
          
          // Force update after modal is shown
          this.$nextTick(() => {
            this.$forceUpdate();
            console.log('🔍 [Admin.vue] Forced update after modal show');
          });
        });
      },
    
          resetPermissionForm() {
        this.permissionForm = {
          role_name: '',
          name: '',
          description: '',
          permissions: this.getDefaultPermissions(),
          app_access: 'assigned',
          environment_access: { dev: true, staging: true, prod: false }
        };
      },
    
    getDefaultPermissions() {
      // Get all permissions except admin ones
      const allPermissions = [];
      this.permissionCategories.forEach(category => {
        if (category.name !== 'Admin') {
          category.permissions.forEach(permission => {
            allPermissions.push(permission);
          });
        }
      });
      return allPermissions;
    },
    
          updateEnvironmentAccess() {
        // Automatically update environment access based on permissions
        if (this.permissionForm.permissions.includes('admin.all')) {
          this.permissionForm.environment_access = { dev: true, staging: true, prod: true };
        } else if (this.permissionForm.permissions.includes('admin.users') || 
                   this.permissionForm.permissions.includes('admin.roles')) {
          this.permissionForm.environment_access = { dev: true, staging: true, prod: true };
        } else {
          // Default to dev and staging for non-admin roles
          this.permissionForm.environment_access = { dev: true, staging: true, prod: false };
        }
      },
      
      // Convert array to object for environment access
      convertEnvironmentAccessToObject(environmentAccess) {
        if (Array.isArray(environmentAccess)) {
          return {
            dev: environmentAccess.includes('dev'),
            staging: environmentAccess.includes('staging'),
            prod: environmentAccess.includes('prod')
          };
        } else if (typeof environmentAccess === 'object' && environmentAccess !== null) {
          // Already an object, return as is
          return {
            dev: environmentAccess.dev || false,
            staging: environmentAccess.staging || false,
            prod: environmentAccess.prod || false
          };
        } else {
          // Default fallback
          return { dev: true, staging: true, prod: false };
        }
      },
      
      // Convert object back to array for backend
      convertEnvironmentAccessToArray(environmentAccess) {
        const environments = [];
        if (environmentAccess.dev) environments.push('dev');
        if (environmentAccess.staging) environments.push('staging');
        if (environmentAccess.prod) environments.push('prod');
        return environments;
      },
    
    async savePermission() {
      this.saving = true;
      try {
        // Convert environment access object back to array for backend
        const formData = {
          ...this.permissionForm,
          environment_access: this.convertEnvironmentAccessToArray(this.permissionForm.environment_access)
        };
        
        if (this.editingPermission) {
          await adminService.editPermission(this.editingPermission.id, formData);
          this.safeToast('success', 'Role updated successfully');
        } else {
          await adminService.addPermission(formData);
          this.safeToast('success', 'Role added successfully');
        }
        
        $('#permissionModal').modal('hide');
        
        // Clear editing state
        this.editingPermission = null;
        
        // Refresh cache and reload data
        await this.refreshCache();
        
      } catch (error) {
        console.error('Error saving permission:', error);
        this.safeToast('error', 'Failed to save role');
      } finally {
        this.saving = false;
      }
    },
    
    async deletePermission(permissionId) {
      if (!confirm('Are you sure you want to delete this role?')) {
        return;
      }
      
      try {
        await adminService.deletePermission(permissionId);
        this.safeToast('success', 'Role deleted successfully');
        
        // Refresh cache and reload data
        await this.refreshCache();
        
      } catch (error) {
        console.error('Error deleting permission:', error);
        this.safeToast('error', 'Failed to delete role');
      }
    },
    
    showAddGroupMappingModal() {
      this.editingGroupMapping = null;
      this.resetGroupMappingForm();
      $('#groupMappingModal').modal('show');
    },
    
    editGroupMapping(mapping) {
      this.editingGroupMapping = mapping;
      this.groupMappingForm = {
        group_name: mapping.group_name || mapping.groupName,
        role_id: mapping.role_id || mapping.id,
        description: mapping.description || ''
      };
      $('#groupMappingModal').modal('show');
    },
    
    resetGroupMappingForm() {
      this.groupMappingForm = {
        group_name: '',
        role_id: '',
        description: ''
      };
    },
    
    async saveGroupMapping() {
      this.saving = true;
      try {
        // Debug: Log the form data being sent
        console.log('🔍 [Admin.vue] Saving group mapping with data:', this.groupMappingForm);
        console.log('🔍 [Admin.vue] Available permissions:', this.permissionList.map(p => ({ id: p.id, role_id: p.role_id, role_name: p.role_name, name: p.name })));
        
        if (this.editingGroupMapping) {
          await adminService.editGroupMapping(this.editingGroupMapping.id, this.groupMappingForm);
          this.safeToast('success', 'Group mapping updated successfully');
        } else {
          await adminService.addGroupMapping(this.groupMappingForm);
          this.safeToast('success', 'Group mapping added successfully');
        }
        
        $('#groupMappingModal').modal('hide');
        await this.loadGroupMappings();
      } catch (error) {
        console.error('Error saving group mapping:', error);
        this.safeToast('error', 'Failed to save group mapping');
      } finally {
        this.saving = false;
      }
    },
    
    async deleteGroupMapping(mappingId) {
      if (!confirm('Are you sure you want to delete this group mapping?')) {
        return;
      }
      
      try {
        await adminService.deleteGroupMapping(mappingId);
        this.safeToast('success', 'Group mapping deleted successfully');
        await this.loadGroupMappings();
      } catch (error) {
        console.error('Error deleting group mapping:', error);
        this.safeToast('error', 'Failed to delete group mapping');
      }
    },
    
    editAppMapping(app) {
      // This would open a modal for editing app access
      console.log('Edit app mapping:', app);
    },
    
    selectAllPermissions() {
      const allPermissions = [];
      this.permissionCategories.forEach(category => {
        category.permissions.forEach(permission => {
          allPermissions.push(permission);
        });
      });
      this.permissionForm.permissions = allPermissions;
    },
    
    selectNonePermissions() {
      this.permissionForm.permissions = [];
    },
    
    selectDefaultPermissions() {
      this.permissionForm.permissions = this.getDefaultPermissions();
    },
    
    editGroupAppMapping(mapping) {
      console.log('🔍 [Admin.vue] Editing group app mapping:', mapping);
      this.editingGroupAppMapping = mapping;
      
      // Convert from backend structure to form structure
      this.groupAppMappingForm = {
        group_name: mapping.group_name,
        app_access: mapping.app_access || [],
        has_all_access: mapping.has_all_access || false,
        description: mapping.description || ''
      };
      
      console.log('📝 [Admin.vue] Form data:', this.groupAppMappingForm);
      console.log('📝 [Admin.vue] Current app_access:', this.groupAppMappingForm.app_access);
      console.log('📝 [Admin.vue] Current has_all_access:', this.groupAppMappingForm.has_all_access);
      $('#groupAppMappingModal').modal('show');
    },
    
    // Handle has_all_access checkbox change
    onHasAllAccessChange() {
      if (this.groupAppMappingForm.has_all_access) {
        // If has all access is checked, clear the app_access array
        this.groupAppMappingForm.app_access = [];
      }
    },
    
    // Validate group app mapping form data
    validateGroupAppMappingForm() {
      const errors = [];
      
      if (!this.groupAppMappingForm.group_name) {
        errors.push('Group name is required');
      }
      
      if (this.groupAppMappingForm.has_all_access) {
        // Universal access - ensure no specific apps are selected
        if (this.groupAppMappingForm.app_access && this.groupAppMappingForm.app_access.length > 0) {
          console.log('🔄 [Admin.vue] Validation: Universal access enabled, clearing specific apps');
          this.groupAppMappingForm.app_access = [];
        }
      } else {
        // Specific access - ensure at least one app is selected
        if (!this.groupAppMappingForm.app_access || this.groupAppMappingForm.app_access.length === 0) {
          errors.push('Please select at least one app or check "Has access to all apps"');
        }
      }
      
      return errors;
    },
    
    async saveGroupAppMapping() {
      this.saving = true;
      try {
        // Validate form data
        const errors = this.validateGroupAppMappingForm();
        if (errors.length > 0) {
          this.safeToast('error', errors.join('\n'));
          return;
        }
        
        // Ensure app_access is properly formatted
        const formData = {
          ...this.groupAppMappingForm,
          app_access: this.groupAppMappingForm.has_all_access ? [] : (this.groupAppMappingForm.app_access || [])
        };
        
        if (this.editingGroupAppMapping) {
          await adminService.editGroupAppMapping(this.editingGroupAppMapping.id, formData);
          this.safeToast('success', 'Group app mapping updated successfully');
        } else {
          await adminService.addGroupAppMapping(formData);
          this.safeToast('success', 'Group app mapping added successfully');
        }
        
        $('#groupAppMappingModal').modal('hide');
        await this.loadGroupAppMappings();
      } catch (error) {
        console.error('Error saving group app mapping:', error);
        this.safeToast('error', 'Failed to save group app mapping');
      } finally {
        this.saving = false;
      }
    },
    
    async deleteGroupAppMapping(mappingId) {
      if (!confirm('Are you sure you want to delete this group app mapping?')) {
        return;
      }
      
      try {
        await adminService.deleteGroupAppMapping(mappingId);
        this.safeToast('success', 'Group app mapping deleted successfully');
        await this.loadGroupAppMappings();
      } catch (error) {
        console.error('Error deleting group app mapping:', error);
        this.safeToast('error', 'Failed to delete group app mapping');
      }
    },
    
    showAddGroupAppMappingModal() {
      this.editingGroupAppMapping = null;
      this.resetGroupAppMappingForm();
      $('#groupAppMappingModal').modal('show');
    },
    
    resetGroupAppMappingForm() {
      this.groupAppMappingForm = {
        group_name: '',
        app_access: [],
        has_all_access: false,
        description: ''
      };
    },

    // Email Mapping Methods
    async loadEmailMappings() {
      try {
        const response = await adminService.getAllEmailMappings();
        
        if (response.data && response.data.status === 200) {
          this.emailMappings = response.data.mappings || [];
        } else {
          this.emailMappings = [];
        }
      } catch (error) {
        console.error('Error loading email mappings:', error);
        this.emailMappings = [];
      }
    },

    showAddEmailMappingModal() {
      this.editingEmailMapping = null;
      this.emailMappingForm = {
        email: '',
        role_id: '',
        description: ''
      };
      $('#emailMappingModal').modal('show');
    },

    editEmailMapping(mapping) {
      this.editingEmailMapping = mapping;
      this.emailMappingForm = {
        email: mapping.email,
        role_id: mapping.role_id,
        description: mapping.description || ''
      };
      $('#emailMappingModal').modal('show');
    },

    async saveEmailMapping() {
      if (!this.emailMappingForm.email || !this.emailMappingForm.role_id) {
        this.safeToast('error', 'Email and role are required');
        return;
      }

      this.saving = true;
      try {
        // Find the selected role to get role_name
        const selectedRole = this.availableRoles.find(role => role.role_id === this.emailMappingForm.role_id);
        if (!selectedRole) {
          this.safeToast('error', 'Selected role not found');
          return;
        }

        const mappingData = {
          email: this.emailMappingForm.email,
          role_id: this.emailMappingForm.role_id,
          role_name: selectedRole.role_name,
          description: this.emailMappingForm.description
        };

        if (this.editingEmailMapping) {
          // Update existing mapping
          await adminService.updateEmailMapping(this.editingEmailMapping.id, mappingData);
          this.safeToast('success', 'Email mapping updated successfully');
        } else {
          // Add new mapping
          await adminService.addEmailMapping(mappingData);
          this.safeToast('success', 'Email mapping added successfully');
        }
        
        $('#emailMappingModal').modal('hide');
        
        // Add a small delay to ensure backend has processed the change
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await this.loadEmailMappings();
      } catch (error) {
        console.error('Error saving email mapping:', error);
        this.safeToast('error', 'Failed to save email mapping');
      } finally {
        this.saving = false;
      }
    },

    async deleteEmailMapping(mappingId) {
      if (!confirm('Are you sure you want to delete this email mapping?')) {
        return;
      }

      try {
        await adminService.deleteEmailMapping(mappingId);
        this.safeToast('success', 'Email mapping deleted successfully');
        
        // Add a small delay to ensure backend has processed the change
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await this.loadEmailMappings();
      } catch (error) {
        console.error('Error deleting email mapping:', error);
        this.safeToast('error', 'Failed to delete email mapping');
      }
    },

    formatRoleName(role) {
      return role.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      
      try {
        // Handle Firestore Timestamp objects
        if (dateString && typeof dateString === 'object' && dateString.toDate) {
          const date = dateString.toDate();
          return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        
        // Handle Date objects
        if (dateString instanceof Date) {
          return dateString.toLocaleDateString() + ' ' + dateString.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        
        // Handle string dates
        const date = new Date(dateString);
        
        // Check if the date is valid
        if (isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (error) {
        console.error('Error formatting date:', dateString, error);
        return 'Invalid Date';
      }
    },

    async refreshEmailMappings() {
      this.refreshing = true;
      try {
        // First refresh the backend cache
        await adminService.refreshEmailMappings();
        
        // Then reload the data
        await this.loadEmailMappings();
        
        this.safeToast('success', 'Email mappings refreshed successfully');
      } catch (error) {
        console.error('Error refreshing email mappings:', error);
        this.safeToast('error', 'Failed to refresh email mappings');
      } finally {
        this.refreshing = false;
      }
    },

    // Email App Access Mapping Methods
    async loadEmailAppAccessMappings() {
      try {
        const response = await adminService.getAllEmailAppAccessMappings();
        if (response.data && response.data.status === 200) {
          this.emailAppAccessMappings = response.data.result || [];
        } else {
          this.emailAppAccessMappings = [];
        }
      } catch (error) {
        console.error('Error loading email app access mappings:', error);
        this.emailAppAccessMappings = [];
      }
    },

    async refreshEmailAppAccessMappings() {
      try {
        await adminService.refreshEmailAppAccessMappings();
        await this.loadEmailAppAccessMappings();
        this.safeToast('success', 'Email app access mappings refreshed successfully');
      } catch (error) {
        console.error('Error refreshing email app access mappings:', error);
        this.safeToast('error', 'Failed to refresh email app access mappings');
      }
    },

    showAddEmailAppAccessMappingModal() {
      this.editingEmailAppAccessMapping = null;
      this.resetEmailAppAccessMappingForm();
      $('#emailAppAccessMappingModal').modal('show');
    },

    editEmailAppAccessMapping(mapping) {
      this.editingEmailAppAccessMapping = mapping;
      this.emailAppAccessMappingForm = {
        email: mapping.email,
        app_access: mapping.app_access ? [...mapping.app_access] : [],
        has_all_access: mapping.has_all_access || false,
        description: mapping.description || ''
      };
      $('#emailAppAccessMappingModal').modal('show');
    },

    resetEmailAppAccessMappingForm() {
      this.emailAppAccessMappingForm = {
        email: '',
        app_access: [],
        has_all_access: false,
        description: ''
      };
    },

    onEmailHasAllAccessChange() {
      if (this.emailAppAccessMappingForm.has_all_access) {
        this.emailAppAccessMappingForm.app_access = [];
      }
    },

    async saveEmailAppAccessMapping() {
      if (!this.emailAppAccessMappingForm.email) {
        this.safeToast('error', 'Email is required');
        return;
      }

      if (!this.emailAppAccessMappingForm.has_all_access && 
          (!this.emailAppAccessMappingForm.app_access || this.emailAppAccessMappingForm.app_access.length === 0)) {
        this.safeToast('error', 'Please select at least one app or enable all apps access');
        return;
      }

      this.saving = true;
      try {
        const mappingData = {
          email: this.emailAppAccessMappingForm.email.toLowerCase().trim(),
          has_all_access: this.emailAppAccessMappingForm.has_all_access,
          app_access: this.emailAppAccessMappingForm.has_all_access ? [] : this.emailAppAccessMappingForm.app_access,
          description: this.emailAppAccessMappingForm.description
        };

        if (this.editingEmailAppAccessMapping) {
          // Update existing mapping
          await adminService.updateEmailAppAccessMapping(this.editingEmailAppAccessMapping.id, mappingData);
          this.safeToast('success', 'Email app access mapping updated successfully');
        } else {
          // Add new mapping
          await adminService.addEmailAppAccessMapping(mappingData);
          this.safeToast('success', 'Email app access mapping added successfully');
        }
        
        $('#emailAppAccessMappingModal').modal('hide');
        
        // Add a small delay to ensure backend has processed the change
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await this.loadEmailAppAccessMappings();
      } catch (error) {
        console.error('Error saving email app access mapping:', error);
        this.safeToast('error', 'Failed to save email app access mapping');
      } finally {
        this.saving = false;
      }
    },

    async deleteEmailAppAccessMapping(mappingId) {
      if (!confirm('Are you sure you want to delete this email app access mapping?')) {
        return;
      }

      try {
        await adminService.deleteEmailAppAccessMapping(mappingId);
        this.safeToast('success', 'Email app access mapping deleted successfully');
        await this.loadEmailAppAccessMappings();
      } catch (error) {
        console.error('Error deleting email app access mapping:', error);
        this.safeToast('error', 'Failed to delete email app access mapping');
      }
    },

    getRoleNameForDisplay(mapping) {
      // First try to get role_name from the mapping itself
      if (mapping.role_name) {
        return mapping.role_name;
      }
      
      // If no role_name, try to find it from permissionList using role_id
      if (mapping.role_id) {
        const permission = this.permissionList.find(p => 
          (p.role_id || p.id) === mapping.role_id
        );
        if (permission) {
          return permission.name || permission.role_name || permission.roleName || permission.id;
        }
      }
      
      // Fallback to role_id if nothing else works
      return mapping.role_id || 'Unknown Role';
    }
  }
};
</script>

<style scoped>
.content{
  width: 100%;
}

.modal-content {
  background-color: #2b2b2b;
  color: #ffffff;
}

.modal-header {
  border-bottom: 1px solid #444;
}

.modal-footer {
  border-top: 1px solid #444;
}

.form-control {
  background-color: #3a3a3a;
  border: 1px solid #555;
  color: #ffffff;
}

.form-control:focus {
  background-color: #3a3a3a;
  border-color: #007bff;
  color: #ffffff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Override global checkbox hiding rule */
.modal .form-check-input[type="checkbox"] {
  display: inline-block !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: relative !important;
  width: 16px !important;
  height: 16px !important;
  margin-right: 8px !important;
  background-color: #3a3a3a !important;
  border: 1px solid #555 !important;
  border-radius: 3px !important;
  cursor: pointer !important;
}

.modal .form-check-input[type="checkbox"]:checked {
  background-color: #007bff !important;
  border-color: #007bff !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e") !important;
  background-size: 12px !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
}

.modal .form-check-input[type="checkbox"]:focus {
  outline: none !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
}

.form-check-label {
  padding-left: 0;
  color: #ffffff;
  margin-left: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

/* Two-column grid for permissions */
.permission-categories.permissions-grid {
  display: flex;
  gap: 32px;
  margin-bottom: 0;
}
.permissions-column {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.permission-category {
  min-width: 260px;
  margin-bottom: 0;
  padding: 20px 18px 16px 18px;
  background: linear-gradient(135deg, #23272f 80%, #23272f 100%);
  border: 1.5px solid #393e4a;
  border-radius: 14px;
  box-shadow: 0 2px 12px 0 rgba(20, 20, 20, 0.10);
  transition: box-shadow 0.2s;
}

.permission-category:hover {
  box-shadow: 0 4px 24px 0 rgba(0, 123, 255, 0.10);
  border-color: #007bff;
}

.permission-category h6 {
  color: #4ea1ff;
  margin-bottom: 16px;
  font-weight: 700;
  font-size: 1.08rem;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #393e4a;
  padding-bottom: 8px;
  text-transform: uppercase;
}

.permission-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
@media (max-width: 900px) {
  .permission-categories.permissions-grid {
    flex-direction: column;
    gap: 0;
  }
  .permissions-column {
    gap: 0;
  }
}

.permission-buttons {
  margin-bottom: 20px;
  padding: 15px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid #444;
}

.permission-buttons .btn {
  margin-right: 10px;
  margin-bottom: 5px;
}

.table {
  background-color: #2b2b2b;
}

.table th {
  background-color: #3a3a3a;
  border-color: #555;
  color: #ffffff;
}

.table td {
  border-color: #555;
  color: #ffffff;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(255, 255, 255, 0.05);
}

.badge {
  font-size: 0.75em;
  padding: 4px 8px;
}

.badge-info {
  background-color: #17a2b8;
  color: #ffffff;
}

.badge-success {
  background-color: #28a745;
  color: #ffffff;
}

.badge-primary {
  background-color: #007bff;
  color: #ffffff;
}

/* Multi-select styling */
select[multiple] {
  background-color: #3a3a3a;
  border: 1px solid #555;
  color: #ffffff;
}

select[multiple] option {
  background-color: #3a3a3a;
  color: #ffffff;
  padding: 8px 12px;
}

select[multiple] option:checked {
  background-color: #007bff;
  color: #ffffff;
}

select[multiple] option:hover {
  background-color: #555;
}

/* Disabled state styling */
select[multiple][disabled] {
  background-color: #3a3a3a;
  border: 1px solid #555;
  color: #888;
  cursor: not-allowed;
}

select[multiple][disabled] option {
  background-color: #3a3a3a;
  color: #888;
  cursor: not-allowed;
}

/* Warning badge style */
.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

/* Group App Mapping Form Specific Styles */
.form-check {
  display: flex;
  align-items: center;
  margin: 10px 0;
  padding-left: 0;
}

.form-check .form-check-input {
  margin-right: 8px;
  margin-left: 0;
  position: static !important;
}

.form-check .form-check-label {
  margin-bottom: 0;
  display: inline-block;
  vertical-align: middle;
}
</style>